export interface ReadingStage {
  value: number
  label: string
  simpleName: string
  simpleDescription: string
  technicalName: string
  technicalDescription: string
  skills: string[]
  definingSkill: string
}

export const stages: ReadingStage[] = [
  {
    value: 0,
    label: 'pre-reader',
    simpleName: 'Getting ready to read',
    simpleDescription:
      'Rhyming, recognizing letters, and learning that print tells a story',
    technicalName: 'Pre-Reader',
    technicalDescription:
      'Concepts of print, phonological awareness, letter recognition',
    skills: ['rhyme', 'syllables', 'letter-recognition', 'print-awareness'],
    definingSkill: 'letter-recognition',
  },
  {
    value: 1,
    label: 'early-beginner',
    simpleName: 'Cracking the code',
    simpleDescription:
      'Sounding out simple three-letter words like cat, dog, and sit',
    technicalName: 'Early Beginning Reader (Stage 2A)',
    technicalDescription:
      'Letter-sound correspondences, CVC blending, tap-blend-swipe',
    skills: ['letter-sounds', 'cvc-blending', 'short-vowels'],
    definingSkill: 'cvc-blending',
  },
  {
    value: 2,
    label: 'late-beginner',
    simpleName: 'Building fluency',
    simpleDescription:
      'Reading words with letter teams like sh and ch, and blends like st and cl',
    technicalName: 'Late Beginning Reader (Stage 2B)',
    technicalDescription: 'Digraphs, consonant blends, basic fluency',
    skills: ['digraphs', 'consonant-blends', 'basic-fluency'],
    definingSkill: 'digraphs-and-blends',
  },
  {
    value: 3,
    label: 'early-transitional',
    simpleName: 'Spotting patterns',
    simpleDescription:
      'Reading longer words by noticing patterns like the silent e in cake and bike',
    technicalName: 'Early Transitional Reader (Stage 3A)',
    technicalDescription:
      'Silent-e, VCe patterns, inflected endings, two-syllable words',
    skills: ['silent-e', 'vce-patterns', 'inflected-endings', 'two-syllable'],
    definingSkill: 'silent-e',
  },
  {
    value: 4,
    label: 'late-transitional',
    simpleName: 'Reading with confidence',
    simpleDescription:
      'Tackling trickier words like rain, boat, and light',
    technicalName: 'Late Transitional Reader (Stage 3B)',
    technicalDescription:
      'Vowel teams, complex vowels, advanced two-syllable decoding',
    skills: ['vowel-teams', 'complex-vowels', 'advanced-syllables'],
    definingSkill: 'vowel-teams',
  },
  {
    value: 5,
    label: 'fluent',
    simpleName: 'Reading to learn',
    simpleDescription:
      'Reading chapter books independently and learning new vocabulary from context',
    technicalName: 'Fluent Reader (Stage 4)',
    technicalDescription:
      'R-controlled vowels, morphology, trigraphs, silent letters, fluent decoding',
    skills: [
      'r-controlled',
      'morphology',
      'trigraphs',
      'silent-letters',
      'fluent-decoding',
    ],
    definingSkill: 'r-controlled',
  },
]

export function getStageByValue(value: number): ReadingStage | undefined {
  return stages.find((s) => s.value === value)
}

export function getStageByLabel(label: string): ReadingStage | undefined {
  return stages.find((s) => s.label === label)
}

// ---------------------------------------------------------------------------
// Parent-facing stage display names (May 2026 evaluation framework)
// ---------------------------------------------------------------------------
// Sourced from specs/evaluation-v2/eval-handoffMay2026/Hopscotch_Evaluation_Framework_V3.md §2
// and specs/evaluation-v2/placement-result-handoff-May2026/PlacementResults—StructureSpec.md §2.
//
// `name` is the snappy parent-facing identity ("Code Cracker"); `tag` is the
// chip caption that pairs with the stage-of-six pill on the placement page.
//
// Used by: evaluation results screen, placement results page, header pill,
// and anywhere parent-facing copy needs the stage identity.
//
// Additive — do NOT change the existing `simpleName` / `technicalName` on the
// `stages` array. Other consumers (legacy SavedResultsView, downstream
// modules) still read those fields.

export type StageLabel =
  | 'pre-reader'
  | 'early-beginner'
  | 'late-beginner'
  | 'early-transitional'
  | 'late-transitional'
  | 'fluent'

export interface StageDisplay {
  name: string
  tag: string
}

export const STAGE_DISPLAY: Record<StageLabel, StageDisplay> = {
  'pre-reader':         { name: 'Sound Explorer',   tag: 'Hearing the sounds in words' },
  'early-beginner':     { name: 'Code Cracker',     tag: 'Letters meet sounds' },
  'late-beginner':      { name: 'Word Builder',     tag: 'Sounding out short words' },
  'early-transitional': { name: 'Pattern Spotter',  tag: 'Cracking spelling patterns' },
  'late-transitional':  { name: 'Story Reader',     tag: 'Reading whole stories' },
  'fluent':             { name: 'Book Adventurer',  tag: 'Diving into chapter books' },
}

export function getStageDisplay(label: string): StageDisplay | undefined {
  return STAGE_DISPLAY[label as StageLabel]
}
